import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Grid, TextField, MenuItem, Button,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Tabs, Tab, Divider, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = '/api/contabilidad';

const fmt = v => parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtFecha = s => {
  if (!s) return '';
  const [y, m, d] = String(s).substring(0, 10).split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : String(s).substring(0, 10);
};
const fmtMes = p => {
  if (!p || p.length !== 6) return p || '';
  const mn = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${mn[parseInt(p.substring(4,6))-1]} ${p.substring(0,4)}`;
};

const TIPO_SUNAT = {
  '01': 'Factura', '03': 'Boleta', '04': 'Liquidación',
  '07': 'Nota Crédito', '08': 'Nota Débito', '09': 'Guía Remisión',
  '12': 'Ticket POS', '20': 'C. Retención', '40': 'C. Percepción',
};

const COL_TOTALS_STYLE = {
  fontWeight: 'bold', bgcolor: '#e3f2fd', fontFamily: 'monospace', fontSize: 12
};

// Definición de columnas compartida entre la tabla en pantalla, Excel y PDF — formato
// oficial SUNAT 8.1 (Registro de Compras) / 14.1 (Registro de Ventas e Ingresos), con
// las 3 columnas de prorrateo en Compras (gravada exclusiva / mixta / no gravada) y la
// columna de Ventas de Exportación en Ventas, según clasificación destino_operacion.
function columnasOficiales(esCompras) {
  const comunes = [
    { key: 'fecha_vto', h: 'Fecha Vcto.', get: r => fmtFecha(r.doc_fecha_vencimiento), num: false, w: 12 },
    { key: 'doc_id', h: 'Doc. Identidad', get: r => r.num_documento || '', num: false, w: 14 },
  ];
  const compras = [
    { key: 'spot_fecha', h: 'Fecha SPOT', get: r => fmtFecha(r.det_fecha), num: false, w: 12 },
    { key: 'spot_num', h: 'Número SPOT', get: r => r.det_numero || '', num: false, w: 14 },
    { key: 'bg_excl', h: 'B.Imp Grav.Exclusiva', get: r => r.base_gravada_exclusiva, num: true, w: 15 },
    { key: 'igv_excl', h: 'IGV Exclusiva', get: r => r.igv_exclusiva, num: true, w: 13 },
    { key: 'bg_mixta', h: 'B.Imp Grav.+No Grav.', get: r => r.base_gravada_mixta, num: true, w: 15 },
    { key: 'igv_mixta', h: 'IGV Mixta', get: r => r.igv_mixta, num: true, w: 13 },
    { key: 'bg_nogr', h: 'B.Imp No Gravada', get: r => r.base_gravada_no_gravada, num: true, w: 15 },
    { key: 'adquis', h: 'Adquisiciones No Gravadas', get: r => r.base_inafecta, num: true, w: 16 },
    { key: 'isc', h: 'ISC', get: r => r.isc, num: true, w: 11 },
    { key: 'otros', h: 'Otros Tributos', get: r => r.otros_tributos, num: true, w: 13 },
    { key: 'total', h: 'Precio de Compra', get: r => r.total, num: true, w: 15 },
  ];
  const ventas = [
    { key: 'ventas_exp', h: 'Ventas Exportación', get: r => r.ventas_exportacion, num: true, w: 15 },
    { key: 'bg_normal', h: 'B.Imp Ope.Grav.', get: r => r.base_gravada_normal, num: true, w: 14 },
    { key: 'exonerado', h: 'Exonerado', get: r => r.base_exonerada, num: true, w: 12 },
    { key: 'inafecto', h: 'Inafecto', get: r => r.base_inafecta, num: true, w: 12 },
    { key: 'isc', h: 'ISC', get: r => r.isc, num: true, w: 11 },
    { key: 'igv_normal', h: 'IGV', get: r => r.igv_normal, num: true, w: 12 },
    { key: 'otros', h: 'Otros', get: r => r.otros_tributos, num: true, w: 11 },
    { key: 'total', h: 'Precio de Venta', get: r => r.total, num: true, w: 14 },
  ];
  const finales = [
    { key: 'tc', h: 'T.C.', get: r => r.tipo_cambio ? parseFloat(r.tipo_cambio).toFixed(3) : '', num: false, w: 9 },
    { key: 'ref', h: 'Ref.Doc. que modifica', get: r => r.doc_ref_serie ? `${r.doc_ref_serie}-${r.doc_ref_numero || ''}` : '', num: false, w: 16 },
  ];
  return [...comunes, ...(esCompras ? compras : ventas), ...finales];
}

export function TabRegistro({ tipo, empresaId, empresa, periodos }) {
  const esCompras = tipo === 'CO';
  const colsExtra = columnasOficiales(esCompras);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Al cambiar empresa, poner el período abierto actual como fecha por defecto
  useEffect(() => {
    if (!periodos.length) return;
    const abiertos = periodos.filter(p => p.estado === 'ABIERTO').sort((a,b) => a.periodo.localeCompare(b.periodo));
    if (abiertos.length > 0) {
      const p = abiertos[0].periodo;
      const anio = p.substring(0,4), mes = p.substring(4,6);
      const ultimoDia = new Date(parseInt(anio), parseInt(mes), 0).getDate();
      setFechaDesde(`${anio}-${mes}-01`);
      setFechaHasta(`${anio}-${mes}-${String(ultimoDia).padStart(2,'0')}`);
    }
    setData(null);
  }, [periodos, empresaId]);

  const buscar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const params = { empresa_id: empresaId, tipo };
      if (periodo)     params.periodo    = periodo;
      if (fechaDesde)  params.fecha_desde = fechaDesde;
      if (fechaHasta)  params.fecha_hasta = fechaHasta;
      const r = await axios.get(`${API}/reportes/registro-compras-ventas`, { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al generar reporte');
    }
    setLoading(false);
  }, [empresaId, tipo, periodo, fechaDesde, fechaHasta]);

  const exportarExcel = () => {
    if (!data?.rows?.length) return;
    const titulo = esCompras ? 'Registro de Compras' : 'Registro de Ventas';
    const extra = columnasOficiales(esCompras);
    const headers = [
      'N°','Fecha Emisión','Período','Tipo Doc','Serie','Número',
      esCompras ? 'Proveedor' : 'Cliente',
      ...extra.map(c => c.h),
      'Moneda','Estado'
    ];
    const rows = data.rows.map((r, i) => [
      i+1, fmtFecha(r.fecha), fmtMes(r.periodo),
      TIPO_SUNAT[r.doc_tipo_sunat] || r.doc_tipo_sunat || r.tipo_doc_desc || '',
      r.doc_serie || '', r.doc_numero || '',
      r.auxiliar_nombre || r.glosa || '',
      ...extra.map(c => c.get(r)),
      r.moneda || 'PEN', r.estado || ''
    ]);
    // Fila de totales (solo en las columnas numéricas de montos)
    const totalesRow = new Array(headers.length).fill('');
    totalesRow[6] = 'TOTALES';
    extra.forEach((c, idx) => {
      if (!c.num) return;
      const key = { bg_excl: 'base_gravada_exclusiva', igv_excl: 'igv_exclusiva',
        bg_mixta: 'base_gravada_mixta', igv_mixta: 'igv_mixta',
        bg_nogr: 'base_gravada_no_gravada', adquis: 'base_inafecta',
        isc: 'isc', otros: 'otros_tributos', total: 'total',
        ventas_exp: 'ventas_exportacion', bg_normal: 'base_gravada_normal',
        exonerado: 'base_exonerada', inafecto: 'base_inafecta', igv_normal: 'igv_normal',
      }[c.key];
      const sum = data.rows.reduce((s, r) => s + (parseFloat(c.get(r)) || 0), 0);
      totalesRow[7 + idx] = +sum.toFixed(2);
    });
    rows.push(totalesRow);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [5, 12, 10, 14, 8, 12, 36, ...extra.map(c => c.w), 7, 9].map(w => ({ wch: w }));
    // Estilo negrita en totales
    const lastRow = rows.length + 1;
    const colLetter = n => { let s = ''; n += 1; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };
    for (let i = 6; i < headers.length; i++) {
      const cell = ws[`${colLetter(i)}${lastRow}`];
      if (cell) cell.s = { font: { bold: true } };
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, titulo.substring(0,31));
    const fname = `${titulo.replace(/ /g,'_')}_${fechaDesde || periodo}_${fechaHasta || ''}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  const exportarPdf = () => {
    if (!data?.rows?.length) return;
    const titulo = esCompras ? 'REGISTRO DE COMPRAS' : 'REGISTRO DE VENTAS';
    const extra = columnasOficiales(esCompras);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(13);
    doc.text(titulo, 14, 14);
    doc.setFontSize(9);
    doc.text(`${empresa?.razon_social || ''} | ${fechaDesde ? `${fmtFecha(fechaDesde)} al ${fmtFecha(fechaHasta)}` : fmtMes(periodo)}`, 14, 21);
    doc.text(`Total registros: ${data.rows.length}`, 14, 27);
    autoTable(doc, {
      startY: 32,
      head: [[
        'N°','Fecha','Tipo','Serie-Número', esCompras ? 'Proveedor' : 'Cliente',
        ...extra.map(c => c.h), 'Mon.'
      ]],
      body: [
        ...data.rows.map((r, i) => [
          i+1,
          fmtFecha(r.fecha),
          TIPO_SUNAT[r.doc_tipo_sunat] || r.doc_tipo_sunat || '',
          `${r.doc_serie || ''}-${r.doc_numero || ''}`,
          (r.auxiliar_nombre || r.glosa || '').substring(0,30),
          ...extra.map(c => c.num ? fmt(c.get(r)) : (c.get(r) || '')),
          r.moneda || 'PEN'
        ]),
        [
          '','','','','TOTALES',
          ...extra.map(c => c.num ? fmt(data.rows.reduce((s, r) => s + (parseFloat(c.get(r)) || 0), 0)) : ''),
          ''
        ]
      ],
      styles: { fontSize: 6, cellPadding: 1 },
      headStyles: { fillColor: esCompras ? [25,118,210] : [56,142,60], fontSize: 6 },
      columnStyles: Object.fromEntries(
        extra.map((c, idx) => [5 + idx, { halign: c.num ? 'right' : 'left' }])
      ),
      foot: [],
      didParseCell: (hookData) => {
        if (hookData.row.index === data.rows.length) {
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.fillColor = [227,242,253];
        }
      }
    });
    const fname = `${titulo.replace(/ /g,'_')}_${fechaDesde || periodo}.pdf`;
    doc.save(fname);
  };

  const color = esCompras ? 'primary' : 'success';

  return (
    <Box>
      {/* Filtros */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm="auto">
            <TextField select size="small" label="Período" value={periodo}
              onChange={e => {
                const val = e.target.value;
                setPeriodo(val);
                if (val) {
                  setFechaDesde(''); setFechaHasta('');
                } else if (periodo) {
                  // Restaurar fechas del período que estaba seleccionado
                  const anio = periodo.substring(0,4), mes = periodo.substring(4,6);
                  const ultimoDia = new Date(parseInt(anio), parseInt(mes), 0).getDate();
                  setFechaDesde(`${anio}-${mes}-01`);
                  setFechaHasta(`${anio}-${mes}-${String(ultimoDia).padStart(2,'0')}`);
                }
              }}
              sx={{ minWidth: 160 }} disabled={!empresaId}>
              <MenuItem value="">— Por rango de fecha —</MenuItem>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtMes(p.periodo)}</MenuItem>)}
            </TextField>
          </Grid>
          {!periodo && <>
            <Grid item xs={12} sm="auto">
              <TextField size="small" label="Fecha Desde" type="date" value={fechaDesde}
                onChange={e => setFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }} disabled={!empresaId} />
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField size="small" label="Fecha Hasta" type="date" value={fechaHasta}
                onChange={e => setFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }} disabled={!empresaId} />
            </Grid>
          </>}
          <Grid item xs={12} sm="auto">
            <Button variant="contained" color={color}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={buscar} disabled={loading || !empresaId}>
              Generar
            </Button>
          </Grid>
          {data && data.rows.length > 0 && <>
            <Grid item xs="auto">
              <Button variant="outlined" color="success" size="small" startIcon={<DownloadIcon />}
                onClick={exportarExcel}>Excel</Button>
            </Grid>
            <Grid item xs="auto">
              <Button variant="outlined" color="error" size="small" startIcon={<PictureAsPdfIcon />}
                onClick={exportarPdf}>PDF</Button>
            </Grid>
          </>}
        </Grid>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <>
          {/* Tarjetas de totales */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {[
              { label: 'Base Gravada', val: data.totales.base_gravada, color: '#1976d2' },
              { label: 'IGV (18%)',    val: data.totales.igv,          color: '#f57c00' },
              { label: 'Base Exonerada', val: data.totales.base_exonerada, color: '#7b1fa2' },
              { label: 'TOTAL',        val: data.totales.total,         color: esCompras ? '#1565c0' : '#2e7d32', big: true },
            ].map(c => (
              <Paper key={c.label} variant="outlined" sx={{
                p: 1.5, textAlign: 'center', borderTop: `4px solid ${c.color}`, borderRadius: 2,
                flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 180px' }, minWidth: 150,
              }}>
                <Typography variant="caption" color="text.secondary" display="block">{c.label}</Typography>
                <Typography variant={c.big ? 'h6' : 'subtitle1'} fontWeight="bold" sx={{ color: c.color, fontFamily: 'monospace' }}>
                  S/ {fmt(c.val)}
                </Typography>
              </Paper>
            ))}
          </Box>

          {data.rows.length === 0 ? (
            <Alert severity="info">No hay registros para el período/rango seleccionado.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
              <Table size="small" sx={{ minWidth: 1900 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: esCompras ? 'primary.main' : 'success.main' }}>
                    {['N°','Fecha Emisión','Período','Tipo','Serie','Número',
                      esCompras ? 'Proveedor' : 'Cliente',
                      ...colsExtra.map(c => c.h),
                      'Mon.','Estado'
                    ].map((h, idx) => (
                      <TableCell key={`${h}_${idx}`} sx={{ color: 'white', fontWeight: 'bold', fontSize: 11, py: 1, pl: idx === 0 ? 2 : 1, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.map((r, i) => (
                    <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary', pl: 2 }}>{i+1}</TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtFecha(r.fecha)}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{fmtMes(r.periodo)}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        <Tooltip title={TIPO_SUNAT[r.doc_tipo_sunat] || r.tipo_doc_desc || ''}>
                          <Chip label={r.doc_tipo_sunat || '—'} size="small"
                            sx={{ fontSize: 10, height: 20,
                              bgcolor: r.doc_tipo_sunat === '07' ? '#fff3e0' :
                                       r.doc_tipo_sunat === '08' ? '#fce4ec' : '#e3f2fd' }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{r.doc_serie || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{r.doc_numero || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 11, maxWidth: 220,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={r.auxiliar_nombre || r.glosa || ''}>
                          <span>{r.auxiliar_nombre || <em style={{ color: '#aaa' }}>{r.glosa}</em>}</span>
                        </Tooltip>
                        {r.auxiliar_doc && (
                          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                            {r.auxiliar_doc}
                          </Typography>
                        )}
                      </TableCell>
                      {colsExtra.map(c => (
                        <TableCell key={c.key} align={c.num ? 'right' : 'left'}
                          sx={{ fontSize: 11, fontFamily: c.num ? 'monospace' : 'inherit', whiteSpace: 'nowrap' }}>
                          {c.num ? fmt(c.get(r)) : (c.get(r) || '—')}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontSize: 10 }}>
                        <Chip label={r.moneda || 'PEN'} size="small"
                          sx={{ fontSize: 9, height: 18, bgcolor: r.moneda === 'USD' ? '#e8f5e9' : '#e3f2fd' }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={r.estado || 'ACTIVO'} size="small"
                          color={r.estado === 'ANULADO' ? 'error' : 'success'} variant="outlined"
                          sx={{ fontSize: 9, height: 18 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Fila de totales */}
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ ...COL_TOTALS_STYLE, fontSize: 12 }}>TOTALES</TableCell>
                    {colsExtra.map(c => (
                      <TableCell key={c.key} align="right" sx={{ ...COL_TOTALS_STYLE, fontSize: 11 }}>
                        {c.num ? fmt(data.rows.reduce((s, r) => s + (parseFloat(c.get(r)) || 0), 0)) : ''}
                      </TableCell>
                    ))}
                    <TableCell colSpan={2} sx={{ bgcolor: '#e3f2fd' }} />
                  </TableRow>
                </TableBody>
              </Table>
              <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, alignItems: 'center', bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary">
                  {data.count} registro{data.count !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function RegistrosContables() {
  const [tab, setTab] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [empresa, setEmpresa] = useState(null);
  const [periodos, setPeriodos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => {
      setEmpresas(r.data || []);
      if (r.data?.length > 0) setEmpresaId(String(r.data[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    setEmpresa(empresas.find(e => String(e.id) === String(empresaId)) || null);
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      setPeriodos((r.data || []).sort((a,b) => b.periodo.localeCompare(a.periodo)));
    }).catch(() => {});
  }, [empresaId, empresas]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <PointOfSaleIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Registros Contables</Typography>
          <Typography variant="body2" color="text.secondary">
            Reporte de compras y ventas según asientos contables registrados
          </Typography>
        </Box>
        <TextField select size="small" label="Empresa" value={empresaId}
          onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: 240 }}>
          {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social || e.nombre}</MenuItem>)}
        </TextField>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          TabIndicatorProps={{ style: { height: 3 } }}>
          <Tab icon={<ShoppingCartIcon />} iconPosition="start" label="Registro de Compras"
            sx={{ fontWeight: tab === 0 ? 700 : 400 }} />
          <Tab icon={<PointOfSaleIcon />} iconPosition="start" label="Registro de Ventas"
            sx={{ fontWeight: tab === 1 ? 700 : 400, color: tab === 1 ? 'success.main' : undefined }} />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {empresaId ? (
            tab === 0
              ? <TabRegistro key={`co-${empresaId}`} tipo="CO" empresaId={empresaId} empresa={empresa} periodos={periodos} />
              : <TabRegistro key={`ve-${empresaId}`} tipo="VE" empresaId={empresaId} empresa={empresa} periodos={periodos} />
          ) : (
            <Alert severity="info">Seleccione una empresa contable para generar el reporte.</Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
